/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import React, { useState, forwardRef, useRef } from 'react'
import { Button } from '../button';
import { size, get, set, uniqBy } from 'lodash';
import { Input, message, Modal, Select } from 'antd';
import { getStyles, styles as defaultStyles, LABEL_FORMATS } from './styles';
import { getLicAddress, getRawLicense, jparse, PageMedia, stopProp, wait, getConfig } from '../../actions/utils';
import Logo from '../logo';
import QRCode from 'qrcode'
import Config from '../../config';
import moment from 'moment';
import { getOrderStatus } from '../../models/order';

const config = Config();

const ID = "print-modal";

const FORMAT_OPTIONS = [
  { value: LABEL_FORMATS.STANDARD, label: 'Standard Label - 8.5x11' },
  { value: LABEL_FORMATS.ROLL, label: 'Label Roll - 4 x 4' },
];

const getUserDefaultFormat = () => {
  const store = get(window, 'STORE', { getState: () => ({}) });
  const sState = store.getState();
  return get(sState, 'user.labelFormat', LABEL_FORMATS.STANDARD);
};

export const pullAmtOutOfOrder = (order) => {
  const boxcount = get(order, 'data.order.weight', 1);
  const amt = parseInt(boxcount);
  return isNaN(amt) ? 1 : amt;
}

export const PrintLabelsButton = props => {
  const { order } = props;
  const printRef = useRef();

  const defAmt = pullAmtOutOfOrder(order);

  const [state, setState] = useState({
    amt: defAmt,
    orders: [props.order],
    loading: false,
    format: getUserDefaultFormat(),
  })

  const changeAmt = e => {
    stopProp(e);
    const { value } = e.target;
    set(state, 'orders.0.data.order.weight', value);
    set(state, 'amt', value);
    setState({ ...state });
  }

  const changeFormat = value => {
    setState(s => ({ ...s, format: value }));
  }

  const onClick = async () => {
    try {
      const method = get(printRef, 'current.printOrders', async () => 0);
      setState(s => ({ ...s, loading: true }));
      await method();
    } catch (err) {

    } finally {
      setState(s => ({ ...s, loading: false }));
    }
  }

  return (
    <>
      <Button loading={state.loading} onClick={onClick} css={css(defaultStyles.printLabelsBtn)}>
        Print Labels
        <Input size="small" onClick={e => stopProp(e)} value={state.amt} type="number" onChange={changeAmt} />
        <Select
          size="small"
          value={state.format}
          onClick={e => stopProp(e)}
          onChange={changeFormat}
          style={{ width: 170 }}
          getPopupContainer={trigger => trigger.parentNode}
        >
          {FORMAT_OPTIONS.map(opt => (
            <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
          ))}
        </Select>
      </Button>
      <PrintLabels showButton={false} ref={printRef} orders={state.orders} format={state.format} />
    </>
  )
}

export const PrintLabels = forwardRef((props, ref) => {
  const [localFormat, setLocalFormat] = useState(getUserDefaultFormat());
  const format = props.format || localFormat;
  const styles = getStyles(format);

  const [state, setState] = useState({
    showPrint: false,
    orders: props.orders || [],
    codes: [],
  });

  const changeOrderModals = (show = false) => {
    try {
      const orderModals = document.querySelectorAll('.OrderModal');
      const root = document.getElementById('root');

      (orderModals || []).forEach(o => o.style.display = show ? 'block' : 'none');
      root.style.display = show ? 'block' : 'none';
    } catch (err) {
      console.error(err);
    }
  }

  const printOrders = async () => {
    try {
      const orders = [];

      changeOrderModals(false);

      uniqBy((props.orders || []), 'id').map((order) => {
        const amt = pullAmtOutOfOrder(order);
        for (let i = 0; i < amt; i++) {
          orders.push({ ...order, _boxCount: { val: i + 1, total: amt } })
        }
      })

      setState(s => ({ ...s, showPrint: true, orders }));

      const pm = PageMedia();

      if (format === LABEL_FORMATS.ROLL) {
        pm.insert(`
          size: 4in 4in;
          margin: 0;
        `)
      } else {
        pm.insert(`
          size: portrait;
          margin: 0.5in 0;
        `)
      }

      const codes = await Promise.all(orders.map(o => QRCode.toDataURL(`${config.appUrl}/order/${o.id}`)));
      setState(s => ({ ...s, codes }));

      await wait(500);
      const elem = document.getElementsByClassName("print-modal")[0];
      elem.style.position = 'absolute';
      elem.style.overflow = 'visible';
      window.print();
    } catch (err) {
      message.error(`Failed to print: ${err.message}`);
    } finally {
      setState(s => ({ ...s, showPrint: false }))
      changeOrderModals(true)
    }
  };

  const close = () => {
    setState(s => ({ ...s, showPrint: false }));
  }

  set(ref, 'current.printOrders', printOrders);

  const showButton = get(props, 'showButton', true);

  return (
    <>
      {!!size(props.orders) && showButton && (
        <div css={css`
          display: flex;
          flex-direction: column;
          align-items: stretch;
          align-self: flex-start;
        `}>
          <Button title="Print Labels" hoverText={'To print multiple labels for one order, please select the individual order and enter the number of labels you would like to print in the “Print Labels” button.'} onClick={printOrders}></Button>
          {!props.format && (
            <Select
              size="small"
              value={localFormat}
              onChange={setLocalFormat}
              style={{ marginTop: 6 }}
            >
              {FORMAT_OPTIONS.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
              ))}
            </Select>
          )}
        </div>
      )}
      <Modal
        visible={state.showPrint}
        onOk={close}
        onCancel={close}
        mask={false}
        css={css(styles.modal)}
        footer={null}
        closable={false}
        id={ID}
        wrapClassName={ID}
      >
        <div css={css(styles.labelsContainer)}>
          {(state.orders || []).map((order, i) => <LabelRow key={i} order={order} code={state.codes[i]} format={format} />)}
        </div>
      </Modal>
    </>
  )
})

const getAndFormat = (row, keys, join = '<br />') => {
  let str = [];
  keys.map(k => {
    const val = get(row, k);
    if (val !== undefined) {
      str.push(val);
    }
  })
  return str.join(join);
}

const LabelRow = props => {
  const { order, code, format } = props;
  const styles = getStyles(format);

  const PICKLN = get(order, 'data.order.pickupLN');
  const DESTLN = get(order, 'data.order.destinationLN');
  const route = get(order, 'data.order.route', '{}');
  const bcount = get(order, '_boxCount');
  const { canPrintLabel, title, allowedStatus } = getOrderStatus(order);

  const pick = getRawLicense(PICKLN, {});
  const dest = getRawLicense(DESTLN, {});

  const WIDTHS = [40, 60];

  const COLS = !canPrintLabel ? [
    [
      { title: 'ORDER ID', key: 'orderId' },
    ],
  ] : [
    [
      { title: 'ORDER ID', key: 'orderId' },
      { title: 'VENDOR', val: `${PICKLN}<br />${getAndFormat(pick, ['Licensee', getConfig('schedule.useDBA', true) ? 'DBA' : null].filter(f => !!f))}` },
      { title: 'SHIP TO', val: `${getAndFormat(dest, ['Licensee', getConfig('schedule.useDBA', true) ? 'DBA' : null].filter(f => !!f))}<br />${getLicAddress(DESTLN) || 'N/A'}` },
      { title: 'BOX COUNT', val: bcount ? `${get(bcount, 'val')} of ${get(bcount, 'total')}` : '1 of 1' },
    ],
    [
      { title: 'ROUTE', val: get(jparse(route), 'name', 'N/A'), style: `font-size: 18px; line-height: 18px; font-weight: bold;` },
      { title: '', val: (<div css={css(styles.qrContainer)}><img src={code} /></div>) }
    ]
  ]

  const customLabelLogo = getConfig('logos.label');
  const hasLabelLogo = getConfig('orders.labelLogo');
  const ltype = hasLabelLogo ? getConfig('orders.labelLogo') : getConfig('logoType', 'orig');
  const widths = {
    nj: 40,
    new: 70,
    gh: 47
  }

  const baseWidth = customLabelLogo ? 130 : get(widths, ltype, 130);
  const logoWidth = format === LABEL_FORMATS.ROLL
    ? Math.round(baseWidth * 0.7)
    : baseWidth;

  return (
    <div css={css(styles.labelContainer, !canPrintLabel && styles.crossout)}>
      <div css={css(styles.logoContainer)}><Logo logoKey={customLabelLogo || hasLabelLogo ? 'orders.labelLogo' : undefined} small={ltype === 'new' ? false : true} width={logoWidth} /></div>
      <div css={css(styles.textContainer)}>
        {COLS.map((col, i) => {
          return (
            <div key={i} css={css(`width: ${WIDTHS[i]}%;`, styles.col)}>
              {col.map(c => {
                const val = c.val || get(order, c.key, 'N/A');
                const ishtml = typeof val == 'string' ? val : undefined
                return (
                  <div key={c.key} css={css(styles.row)}>
                    <div css={css(styles.rowTitle)}>{`${c.title}`}</div>
                    {ishtml ? <div css={css(styles.rowValue, c.style)} dangerouslySetInnerHTML={{ __html: val }} /> : <div css={css(styles.rowValue, c.style)}>{val}</div>}
                  </div>
                )
              })}
            </div>
          )
        })}

        {canPrintLabel && <div css={css(styles.approvaldate)}>{`APPROVED DELIVERY DATE: ${!get(order, 'data.order.deliveryDate') ? 'N/A' : moment(get(order, 'data.order.deliveryDate')).format('L')}`}</div>}

        {!canPrintLabel && <div css={css(styles.printErrorContainer)}>
          <p>{`!!SHIPPING LABEL ERROR!!`}</p>
          <br /><br />
          <p>{`PLEASE CHECK ORDER STATUS:`}</p>
          <p>{`Current Status: (${title})`}</p>
          <p>{`Allowed Status: (${allowedStatus.title})`}</p>
        </div>}
      </div>
    </div>
  )
}
